import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  createElement,
} from 'react';
import { type ConversionPopupTriggerSource } from 'services/mixpanel/types';

/** Matches the dialog's `duration-200` close animation with a safe margin. */
const CLOSE_ANIMATION_MS = 250;

export interface CheckoutConfirmation {
  /** The purchased SKU (read from sessionStorage on return); null if unknown. */
  sku: string | null;
}

/**
 * Popup mode (PLT-4467): 'upgrade' is the default checkout flow; 'change-plan'
 * additionally routes a selection smaller than the current pack to the downgrade
 * confirmation dialog (scheduled at period end) instead of Stripe Checkout.
 */
export type ConversionPopupMode = 'upgrade' | 'change-plan';

export interface ConversionPopupContextValue {
  isOpen: boolean;
  selectedSku: string | undefined;
  /** Entry point that opened the popup, surfaced on CONVERSION_POPUP_VIEWED. */
  triggerSource: ConversionPopupTriggerSource | undefined;
  /** Whether the popup allows downgrades (change-plan) or is upgrade-only. */
  mode: ConversionPopupMode;
  /**
   * Non-null when the popup is showing the post-checkout confirmation view.
   * We trust Stripe's success redirect, so this is a single success state — the
   * billing read-models are reloaded underneath to reflect the new plan.
   */
  confirmation: CheckoutConfirmation | null;
  open: (
    sku?: string,
    triggerSource?: ConversionPopupTriggerSource,
    mode?: ConversionPopupMode,
  ) => void;
  /** Open the popup directly into the post-checkout confirmation view. */
  openConfirmation: (confirmation: CheckoutConfirmation) => void;
  close: () => void;
}

const ConversionPopupContext = createContext<
  ConversionPopupContextValue | undefined
>(undefined);

/**
 * Provider that owns the ConversionPopup open/close + selected-SKU state, plus
 * the post-checkout confirmation state. Mount once near the top of the
 * authenticated tree (Main layout).
 */
export const ConversionPopupProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState<string | undefined>(undefined);
  const [triggerSource, setTriggerSource] = useState<
    ConversionPopupTriggerSource | undefined
  >(undefined);
  const [mode, setMode] = useState<ConversionPopupMode>('upgrade');
  const [confirmation, setConfirmation] = useState<CheckoutConfirmation | null>(
    null,
  );
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelCloseTimer, [cancelCloseTimer]);

  const open = useCallback(
    (
      sku?: string,
      nextTriggerSource?: ConversionPopupTriggerSource,
      nextMode: ConversionPopupMode = 'upgrade',
    ) => {
      cancelCloseTimer();
      setSelectedSku(sku);
      setTriggerSource(nextTriggerSource);
      setMode(nextMode);
      setConfirmation(null);
      setIsOpen(true);
    },
    [cancelCloseTimer],
  );

  const openConfirmation = useCallback(
    (next: CheckoutConfirmation) => {
      cancelCloseTimer();
      // The confirmation view is the post-checkout return, not a trigger-driven
      // open — clear any trigger source carried over from the plan-selection open.
      setTriggerSource(undefined);
      setConfirmation(next);
      setIsOpen(true);
    },
    [cancelCloseTimer],
  );

  const close = useCallback(() => {
    cancelCloseTimer();
    setIsOpen(false);
    // Delay clearing confirmation so the close animation (duration-200) finishes
    // before the dialog body switches — prevents the plan-selection view from
    // flashing in while the dialog is still animating out (PLT-4214).
    closeTimerRef.current = setTimeout(() => {
      setConfirmation(null);
      setTriggerSource(undefined);
      setSelectedSku(undefined);
      setMode('upgrade');
      closeTimerRef.current = null;
    }, CLOSE_ANIMATION_MS);
  }, [cancelCloseTimer]);

  const value = useMemo<ConversionPopupContextValue>(
    () => ({
      isOpen,
      selectedSku,
      triggerSource,
      mode,
      confirmation,
      open,
      openConfirmation,
      close,
    }),
    [
      isOpen,
      selectedSku,
      triggerSource,
      mode,
      confirmation,
      open,
      openConfirmation,
      close,
    ],
  );

  return createElement(ConversionPopupContext.Provider, { value }, children);
};

/**
 * Access the ConversionPopup controller from any component inside the provider.
 *
 * @returns the popup controller (open/close, selected SKU, confirmation state)
 * @throws if called outside `ConversionPopupProvider`
 */
export const useConversionPopup = (): ConversionPopupContextValue => {
  const ctx = useContext(ConversionPopupContext);

  if (!ctx) {
    throw new Error(
      'useConversionPopup must be used within a ConversionPopupProvider',
    );
  }

  return ctx;
};
